import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card } from './ui/card';
import { ShoppingCart, ArrowLeft, Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FALLBACK_EMAIL = 'everything@8eight8.net';

const OrderPage = () => {
  const [params] = useSearchParams();
  const initialBrand = params.get('brand') || '';

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    brand: initialBrand,
    expressions: '',
    quantity: '',
    notes: ''
  });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const update = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'submitting') return;
    setStatus('submitting');
    setErrorMsg('');
    try {
      const composedNotes = [
        form.expressions ? `Expressions: ${form.expressions}` : null,
        form.quantity ? `Quantity: ${form.quantity}` : null,
        form.notes ? `Notes:\n${form.notes}` : null
      ].filter(Boolean).join('\n\n');

      const res = await fetch(`${API_URL}/api/applications/ambassador`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          instagram_handle: '',
          audience_size: `[BRAND ORDER] ${form.brand}`,
          why_message: composedNotes || `Order inquiry for ${form.brand}`
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Submission failed');
      }
      setStatus('success');
      if (typeof window !== 'undefined' && window.__atfTrackLead) {
        window.__atfTrackLead('brand_order_inquiry', { brand: form.brand });
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(typeof err.message === 'string' ? err.message : 'Submission failed');
    }
  };

  const mailtoFallback = `mailto:${FALLBACK_EMAIL}?subject=${encodeURIComponent(`Brand order inquiry: ${form.brand || 'Brand'}`)}&body=${encodeURIComponent(`Brand: ${form.brand}\nName: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\nExpressions: ${form.expressions}\nQuantity: ${form.quantity}\n\nNotes:\n${form.notes}`)}`;

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="order-page">
      <Helmet>
        <title>{form.brand ? `Order — ${form.brand}` : 'Order'} | Aloha Tequila Fest</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="max-w-2xl mx-auto px-4 py-10 sm:py-16">
        <Link to="/brand-guide" data-testid="order-back" className="inline-flex items-center gap-1.5 text-white/55 hover:text-white text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Brand Guide
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-pink-500 to-orange-500 flex items-center justify-center">
            <ShoppingCart className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-black">Order Inquiry</h1>
            <p className="text-white/55 text-sm">
              {form.brand ? <>For: <span className="text-pink-300 font-bold">{form.brand}</span></> : 'Submit a brand request'}
            </p>
          </div>
        </div>

        {status === 'success' ? (
          <Card className="bg-green-500/10 border-green-500/30 p-6 rounded-2xl text-center" data-testid="order-success">
            <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
            <h2 className="text-xl font-black mb-2">Mahalo — request received!</h2>
            <p className="text-white/75 text-sm mb-5 max-w-md mx-auto">
              We&apos;ll review your order inquiry for <span className="text-pink-300 font-bold">{form.brand}</span> and follow up at <span className="text-white">{form.email}</span>.
            </p>
            <Link to="/brand-guide" data-testid="order-success-back" className="inline-flex items-center gap-1.5 text-pink-300 hover:text-pink-200 text-sm">
              Browse more brands <ArrowLeft className="w-4 h-4 rotate-180" />
            </Link>
          </Card>
        ) : (
          <Card className="bg-white/5 border-white/10 p-5 sm:p-7 rounded-2xl">
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="order-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="o-brand" className="text-white/85 text-sm mb-1.5 block">Brand *</Label>
                  <Input
                    id="o-brand"
                    data-testid="order-input-brand"
                    type="text"
                    required
                    value={form.brand}
                    onChange={update('brand')}
                    placeholder="Brand name"
                    className="bg-white/5 border-white/15 text-white"
                  />
                </div>
                <div>
                  <Label htmlFor="o-name" className="text-white/85 text-sm mb-1.5 block">Your name *</Label>
                  <Input id="o-name" data-testid="order-input-name" type="text" required value={form.name} onChange={update('name')} className="bg-white/5 border-white/15 text-white" />
                </div>
                <div>
                  <Label htmlFor="o-email" className="text-white/85 text-sm mb-1.5 block">Email *</Label>
                  <Input id="o-email" data-testid="order-input-email" type="email" required value={form.email} onChange={update('email')} className="bg-white/5 border-white/15 text-white" />
                </div>
                <div>
                  <Label htmlFor="o-phone" className="text-white/85 text-sm mb-1.5 block">Phone</Label>
                  <Input id="o-phone" data-testid="order-input-phone" type="tel" value={form.phone} onChange={update('phone')} className="bg-white/5 border-white/15 text-white" />
                </div>
                <div>
                  <Label htmlFor="o-qty" className="text-white/85 text-sm mb-1.5 block">Quantity / cases</Label>
                  <Input id="o-qty" data-testid="order-input-qty" type="text" value={form.quantity} onChange={update('quantity')} placeholder="e.g. 2 cases / 750mL" className="bg-white/5 border-white/15 text-white" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="o-exp" className="text-white/85 text-sm mb-1.5 block">Expressions of interest</Label>
                  <Input id="o-exp" data-testid="order-input-expressions" type="text" value={form.expressions} onChange={update('expressions')} placeholder="e.g. Blanco, Reposado" className="bg-white/5 border-white/15 text-white" />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="o-notes" className="text-white/85 text-sm mb-1.5 block">Notes</Label>
                  <Textarea id="o-notes" data-testid="order-input-notes" rows={4} value={form.notes} onChange={update('notes')} placeholder="Anything else we should know…" className="bg-white/5 border-white/15 text-white resize-none" />
                </div>
              </div>

              {status === 'error' && (
                <div className="flex items-start gap-2 text-red-300 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="order-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <div>
                    <div>{errorMsg}</div>
                    <a href={mailtoFallback} className="underline mt-1 inline-block">Email us directly instead →</a>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                disabled={status === 'submitting'}
                data-testid="order-submit"
                className="w-full h-12 bg-gradient-to-r from-pink-500 via-orange-500 to-yellow-500 hover:from-pink-600 hover:via-orange-600 hover:to-yellow-600 text-white font-bold rounded-xl inline-flex items-center justify-center gap-2"
              >
                {status === 'submitting' ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</> : <><Send className="w-5 h-5" /> Submit order inquiry</>}
              </Button>

              <p className="text-white/45 text-xs text-center">
                We&apos;ll respond within 1–3 business days. Or email <a href={mailtoFallback} className="text-pink-300 hover:text-pink-200">{FALLBACK_EMAIL}</a> directly.
              </p>
            </form>
          </Card>
        )}
      </div>
    </div>
  );
};

export default OrderPage;
